

import React from "react";
import { ArrowLeft, Download, Calendar, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Meeting } from "../../../services/meeting.service";
import type { Project } from "../../../services/project.service";

interface PreviewHeaderProps {
  projectId: string;
  meetingId: string;
  meeting: Meeting;
  project: Project;
  onOpenExportDialog: () => void;
  isTranslating?: boolean;
}

export function PreviewHeader({ projectId, meetingId, meeting, project, onOpenExportDialog, isTranslating = false }: PreviewHeaderProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation('common');

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div className="preview-header no-print bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/projects/${projectId}/meetings`)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              aria-label="Voltar para lista de atas"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>

            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate">
                  {meeting.name || `Ata ${meeting.number}`}
                </h1>
                <span className="text-gray-400 hidden sm:inline">•</span>
              <span className="text-sm text-gray-600 truncate hidden sm:inline">{project.name}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDate(meeting.date)}</span>
              </div>
              {meeting.location && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1 truncate max-w-32">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{meeting.location}</span>
                  </div>
                </>
              )}
              <Badge
                variant="secondary"
                className={`text-xs px-1.5 py-0 h-5 ${
                  meeting.status === "final"
                    ? "bg-green-100 text-green-700 hover:bg-green-100"
                    : "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                }`}
              >
                {meeting.status === "final" ? "Finalizada" : "Rascunho"}
              </Badge>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={onOpenExportDialog}
              variant="default"
              size="sm"
              className="gap-2"
              disabled={isTranslating}
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{isTranslating ? 'Traduzindo...' : 'Download PDF'}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </>
  );
}

